import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  Badge,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger
} from '@bliks-io/ui';
import { useFilteredItems } from './useFilteredItems';

export interface MultiSelectItem {
  value: string;
  label: string;
  /** Optional leading icon, rendered before the label in items and tags. */
  icon?: React.ReactNode;
  description?: string;
  disabled?: boolean;
}

export interface FormFieldMultiSelectProps {
  /**
   * Field label
   */
  label?: string;

  /**
   * Field description/helper text
   */
  description?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Mark field as required
   */
  required?: boolean;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Selected values
   */
  value: string[];

  /**
   * Change handler
   */
  onChange: (values: string[]) => void;

  /**
   * Available items to select
   */
  items: MultiSelectItem[];

  /**
   * Whether the component is disabled
   */
  disabled?: boolean;

  /**
   * Function to format the selected count text
   */
  selectedTextFormatter?: (count: number) => string;

  /**
   * Custom children to render in the trigger (replaces default selected count)
   */
  renderTrigger?: (selectedItems: MultiSelectItem[]) => React.ReactNode;

  /**
   * Custom renderer for each item
   */
  renderItem?: (item: MultiSelectItem, options: { selected: boolean }) => React.ReactNode;

  /**
   * Field ID
   */
  id?: string;

  /**
   * Field name
   */
  name?: string;

  /**
   * Field orientation
   */
  orientation?: 'vertical' | 'horizontal' | 'responsive';

  /**
   * Additional class for Field wrapper
   */
  fieldClassName?: string;

  /**
   * Additional class for Trigger
   */
  className?: string;

  /**
   * Message to show when no results found
   */
  emptyMessage?: string;

  /**
   * Render a search input in the dropdown and filter items client-side
   * (matches label and description).
   */
  searchable?: boolean;

  /**
   * Placeholder for the search input (only with `searchable`)
   */
  searchPlaceholder?: string;

  /**
   * Render the selected items as removable tags inside the trigger,
   * replacing the selected-count text
   */
  showSelectedTags?: boolean;
}

/**
 * Reusable form field component combining Field + Combobox (multi-select mode)
 * Can be used standalone or wrapped by RHF adapter components
 */
export const FormFieldMultiSelect = React.forwardRef<HTMLButtonElement, FormFieldMultiSelectProps>(
  (
    {
      label,
      description,
      error,
      required = false,
      placeholder,
      value,
      onChange,
      items,
      disabled = false,
      selectedTextFormatter,
      renderTrigger,
      renderItem,
      id,
      name,
      orientation = 'vertical',
      fieldClassName,
      className,
      emptyMessage,
      searchable = false,
      searchPlaceholder,
      showSelectedTags = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const fieldId = id || name;
    const hasError = !!error;

    const defaultEmptyMessage = emptyMessage ?? t('common.noOptionsAvailable');

    const [searchQuery, setSearchQuery] = React.useState('');

    // The Combobox primitive disables cmdk's automatic filtering, so the
    // search filter is applied here over the item data.
    const searchFilter = React.useCallback(
      (item: MultiSelectItem) => {
        if (!searchable || !searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          item.label.toLowerCase().includes(q) ||
          (item.description?.toLowerCase().includes(q) ?? false)
        );
      },
      [searchable, searchQuery]
    );
    const visibleItems = useFilteredItems(items, undefined, searchFilter);

    // Handle value change - ensure it's always an array
    const handleValueChange = React.useCallback(
      (newValue: string | string[] | undefined) => {
        if (Array.isArray(newValue)) {
          onChange(newValue);
        } else {
          onChange([]);
        }
      },
      [onChange]
    );

    // Get selected items for display
    const selectedItems = React.useMemo(() => {
      return items.filter((item) => value.includes(item.value));
    }, [items, value]);

    const removeItem = React.useCallback(
      (itemValue: string) => onChange(value.filter((v) => v !== itemValue)),
      [onChange, value]
    );

    // Render trigger content
    const triggerContent = React.useMemo(() => {
      if (renderTrigger) {
        return renderTrigger(selectedItems);
      }

      if (selectedItems.length === 0) {
        return null;
      }

      if (showSelectedTags) {
        // The trigger is itself a <button>, so the per-tag remove affordance
        // must be a role="button" div (same pattern as the trigger's clear icon).
        return (
          <div className="flex flex-wrap gap-1">
            {selectedItems.map((item) => (
              <Badge key={item.value} colorScheme="neutral" className="gap-1 pr-1">
                {item.icon}
                {item.label}
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`${t('common.Remove')} ${item.label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeItem(item.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      removeItem(item.value);
                    }
                  }}
                  className="cursor-pointer rounded-full p-0.5 hover:bg-surface-strong hover:text-fg-secondary focus:outline-hidden focus:ring-1 focus:ring-ring"
                >
                  <X className="h-3 w-3" />
                </div>
              </Badge>
            ))}
          </div>
        );
      }

      const selectedText = selectedTextFormatter
        ? selectedTextFormatter(selectedItems.length)
        : `${selectedItems.length} selected`;

      return selectedText;
    }, [selectedItems, renderTrigger, selectedTextFormatter, showSelectedTags, t, removeItem]);

    return (
      <Field orientation={orientation} className={fieldClassName} data-invalid={hasError}>
        {label && (
          <FieldLabel htmlFor={fieldId}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </FieldLabel>
        )}
        <FieldContent>
          {description && <FieldDescription>{description}</FieldDescription>}
          <Combobox value={value} onValueChange={handleValueChange} disabled={disabled} multiple>
            <ComboboxTrigger ref={ref} id={fieldId} placeholder={placeholder} className={className}>
              {triggerContent}
            </ComboboxTrigger>
            <ComboboxContent>
              {searchable && (
                <ComboboxInput
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
              )}
              <ComboboxList>
                <ComboboxEmpty>{defaultEmptyMessage}</ComboboxEmpty>
                {visibleItems.map((item) => {
                  const isSelected = value.includes(item.value);

                  // Use custom renderer if provided
                  if (renderItem) {
                    return (
                      <ComboboxItem key={item.value} value={item.value} disabled={item.disabled}>
                        {renderItem(item, { selected: isSelected })}
                      </ComboboxItem>
                    );
                  }

                  // Default rendering - show description if available
                  if (item.description) {
                    return (
                      <ComboboxItem key={item.value} value={item.value} disabled={item.disabled}>
                        <div className="flex items-center gap-2 flex-1">
                          {item.icon}
                          <div className="flex flex-col gap-1">
                            <div className="text-sm">{item.label}</div>
                            <div className="text-xs text-muted-foreground">{item.description}</div>
                          </div>
                        </div>
                      </ComboboxItem>
                    );
                  }

                  // Simple rendering
                  return (
                    <ComboboxItem key={item.value} value={item.value} disabled={item.disabled}>
                      {item.icon ? (
                        <span className="flex items-center gap-2">
                          {item.icon}
                          {item.label}
                        </span>
                      ) : (
                        item.label
                      )}
                    </ComboboxItem>
                  );
                })}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          {error && <FieldError>{error}</FieldError>}
        </FieldContent>
      </Field>
    );
  }
);

FormFieldMultiSelect.displayName = 'FormFieldMultiSelect';
